import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { FaChevronUp, FaCheckCircle, FaRegClock, FaSearch } from "react-icons/fa";
import { MdOutlineEventAvailable, MdOutlineFilterAltOff } from "react-icons/md";
import { RiCalendarEventLine } from "react-icons/ri";
import { HiArrowRight } from "react-icons/hi";
import StudentLayout from "../Layout/index.jsx";
import LightSpinner from "../../../components/LightSpinner.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";

const GET_ALL_CONTESTS = gql`
  query GetAllContestsForStudentView {
    allContests {
      id
      provider
      providerContestId
      title
      url
      startTime
      endTime
      duration
      status
      lastFetchedAt
      lastUpdatedAt
    }
  }
`;

const providerStyles = {
  leetcode: {
    label: "LeetCode",
    badge: "border-amber-200 bg-amber-50 text-amber-900",
    cardBorder: "border-slate-200 hover:border-amber-400",
    cardRing: "hover:ring-2 hover:ring-amber-200/70",
    accentBar: "bg-amber-400",
    iconWrap: "border-amber-200 bg-amber-50",
    softBg: "bg-amber-50/40",
    softBorder: "border-amber-200",
    softText: "text-amber-900",
    timePill: "bg-amber-100 text-amber-900 ring-1 ring-amber-200",
    hoverWash: "from-amber-50/0 to-amber-50/70",
    heroSurface:
      "border-amber-200/80 bg-gradient-to-br from-white via-amber-50/25 to-amber-100/30 shadow-md ring-1 ring-slate-900/5",
    button: "bg-amber-900 hover:bg-amber-950",
    link: "text-amber-800 hover:text-amber-950",
    iconUrl: "https://cdn.simpleicons.org/leetcode/F89F1B",
    poster: {
      canvas: "bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100",
      canvasAlt: "bg-gradient-to-tr from-amber-200/42 via-yellow-100/48 to-orange-200/38",
      orb: "bg-amber-300/45",
      orb2: "bg-orange-400/32",
      accentLine: "from-amber-400 via-orange-400 to-amber-500",
    },
    filterChipActive: "border-amber-500 bg-amber-700 text-white",
  },
  codeforces: {
    label: "Codeforces",
    badge: "border-sky-200 bg-sky-50 text-sky-900",
    cardBorder: "border-slate-200 hover:border-sky-400",
    cardRing: "hover:ring-2 hover:ring-sky-200/70",
    accentBar: "bg-sky-500",
    iconWrap: "border-sky-200 bg-sky-50",
    softBg: "bg-sky-50/40",
    softBorder: "border-sky-200",
    softText: "text-sky-900",
    timePill: "bg-sky-100 text-sky-900 ring-1 ring-sky-200",
    hoverWash: "from-sky-50/0 to-sky-50/70",
    heroSurface:
      "border-sky-200/80 bg-gradient-to-br from-white via-sky-50/25 to-sky-100/30 shadow-md ring-1 ring-slate-900/5",
    button: "bg-sky-900 hover:bg-sky-950",
    link: "text-sky-800 hover:text-sky-950",
    iconUrl: "https://cdn.simpleicons.org/codeforces/1F8ACB",
    poster: {
      canvas: "bg-gradient-to-br from-sky-100 via-blue-50 to-cyan-100",
      canvasAlt: "bg-gradient-to-tr from-sky-200/40 via-cyan-100/45 to-blue-100/35",
      orb: "bg-sky-300/42",
      orb2: "bg-cyan-400/30",
      accentLine: "from-sky-400 via-blue-500 to-cyan-500",
    },
    filterChipActive: "border-sky-600 bg-sky-900 text-white",
  },
  codechef: {
    label: "CodeChef",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-900",
    cardBorder: "border-slate-200 hover:border-emerald-400",
    cardRing: "hover:ring-2 hover:ring-emerald-200/70",
    accentBar: "bg-emerald-500",
    iconWrap: "border-emerald-200 bg-emerald-50",
    softBg: "bg-emerald-50/40",
    softBorder: "border-emerald-200",
    softText: "text-emerald-900",
    timePill: "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200",
    hoverWash: "from-emerald-50/0 to-emerald-50/70",
    heroSurface:
      "border-emerald-200/80 bg-gradient-to-br from-white via-emerald-50/25 to-emerald-100/30 shadow-md ring-1 ring-slate-900/5",
    button: "bg-emerald-900 hover:bg-emerald-950",
    link: "text-emerald-800 hover:text-emerald-950",
    /** White mark on dark posters (masthead / billboard) */
    iconUrlOnDark: "https://cdn.simpleicons.org/codechef/ffffff",
    iconUrl: "https://cdn.simpleicons.org/codechef/5B4638",
    poster: {
      canvas: "bg-gradient-to-br from-teal-100 via-emerald-50 to-emerald-200",
      canvasAlt: "bg-gradient-to-tr from-emerald-300/46 via-teal-100/50 to-cyan-200/42",
      orb: "bg-teal-400/52",
      orb2: "bg-emerald-500/46",
      accentLine: "from-emerald-400 via-teal-400 to-emerald-600",
    },
    filterChipActive: "border-emerald-600 bg-emerald-600 text-white",
  },
  hackerrank: {
    label: "HackerRank",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-950",
    cardBorder: "border-slate-200 hover:border-emerald-400",
    cardRing: "hover:ring-2 hover:ring-emerald-300/70",
    accentBar: "bg-emerald-500",
    iconWrap: "border-emerald-200 bg-emerald-50",
    softBg: "bg-emerald-50/40",
    softBorder: "border-emerald-200",
    softText: "text-emerald-950",
    timePill: "bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200",
    hoverWash: "from-emerald-50/0 to-emerald-50/70",
    heroSurface:
      "border-emerald-200/80 bg-gradient-to-br from-white via-emerald-50/25 to-emerald-50/35 shadow-md ring-1 ring-slate-900/5",
    button: "bg-emerald-800 hover:bg-emerald-950",
    link: "text-emerald-800 hover:text-emerald-950",
    iconUrlOnDark: "https://cdn.simpleicons.org/hackerrank/ffffff",
    iconUrl: "https://cdn.simpleicons.org/hackerrank/2EC866",
    poster: {
      canvas: "bg-gradient-to-br from-emerald-100 via-green-50 to-teal-200",
      canvasAlt: "bg-gradient-to-tr from-emerald-300/46 via-green-100/48 to-teal-200/40",
      orb: "bg-emerald-400/50",
      orb2: "bg-green-500/44",
      accentLine: "from-green-400 via-teal-400 to-emerald-700",
    },
    filterChipActive: "border-emerald-700 bg-emerald-700 text-white",
  },
  topcoder: {
    label: "Topcoder",
    badge: "border-sky-200 bg-sky-50 text-sky-950",
    cardBorder: "border-slate-200 hover:border-sky-500",
    cardRing: "hover:ring-2 hover:ring-sky-200/70",
    accentBar: "bg-sky-500",
    iconWrap: "border-sky-200 bg-sky-50",
    softBg: "bg-sky-50/40",
    softBorder: "border-sky-200",
    softText: "text-sky-950",
    timePill: "bg-sky-100 text-sky-950 ring-1 ring-sky-200",
    hoverWash: "from-sky-50/0 to-sky-50/70",
    heroSurface:
      "border-sky-200/80 bg-gradient-to-br from-white via-sky-50/25 to-cyan-100/25 shadow-md ring-1 ring-slate-900/5",
    button: "bg-sky-800 hover:bg-sky-950",
    link: "text-sky-800 hover:text-sky-950",
    iconUrlOnDark: "https://cdn.simpleicons.org/topcoder/ffffff",
    iconUrl: "https://cdn.simpleicons.org/topcoder/29A8E0",
    poster: {
      canvas: "bg-gradient-to-br from-cyan-100 via-sky-50 to-blue-100",
      canvasAlt: "bg-gradient-to-tr from-sky-300/42 via-cyan-100/45 to-blue-200/42",
      orb: "bg-cyan-400/48",
      orb2: "bg-sky-500/42",
      accentLine: "from-sky-400 via-blue-400 to-cyan-500",
    },
    filterChipActive: "border-sky-700 bg-sky-700 text-white",
  },
  atcoder: {
    label: "AtCoder",
    badge: "border-gray-800 bg-gray-900 text-white",
    cardBorder: "border-slate-200 hover:border-gray-500",
    cardRing: "hover:ring-2 hover:ring-gray-300/60",
    accentBar: "bg-gray-800",
    iconWrap: "border-gray-200 bg-gray-50",
    softBg: "bg-gray-100/60",
    softBorder: "border-gray-300",
    softText: "text-gray-900",
    timePill: "bg-gray-900 text-gray-50 ring-1 ring-gray-300",
    hoverWash: "from-gray-50/0 to-gray-100/70",
    heroSurface:
      "border-gray-200/90 bg-gradient-to-br from-white via-gray-50/35 to-gray-100/35 shadow-md ring-1 ring-slate-900/5",
    button: "bg-gray-900 hover:bg-black",
    link: "text-gray-800 hover:text-black",
    /** Official asset — Simple Icons has no stable AtCoder slug on cdn.simpleicons.org yet */
    iconInvertOnDark: true,
    iconUrl: "https://img.atcoder.jp/assets/atcoder.png",
    poster: {
      canvas: "bg-gradient-to-br from-slate-100/95 via-zinc-100 to-slate-200/88",
      canvasAlt: "bg-gradient-to-tr from-zinc-300/32 via-slate-200/25 to-neutral-200/34",
      orb: "bg-slate-400/30",
      orb2: "bg-zinc-400/24",
      accentLine: "from-gray-500 via-neutral-600 to-zinc-600",
    },
    filterChipActive: "border-gray-800 bg-gray-800 text-white",
  },
  geeksforgeeks: {
    label: "GeeksforGeeks",
    badge: "border-green-200 bg-green-50 text-green-950",
    cardBorder: "border-slate-200 hover:border-green-500",
    cardRing: "hover:ring-2 hover:ring-green-300/65",
    accentBar: "bg-green-600",
    iconWrap: "border-green-200 bg-green-50",
    softBg: "bg-green-50/40",
    softBorder: "border-green-200",
    softText: "text-green-950",
    timePill: "bg-green-100 text-green-950 ring-1 ring-green-200",
    hoverWash: "from-green-50/0 to-green-50/70",
    heroSurface:
      "border-green-200/85 bg-gradient-to-br from-white via-green-50/30 to-green-50/35 shadow-md ring-1 ring-slate-900/5",
    button: "bg-green-800 hover:bg-green-950",
    link: "text-green-800 hover:text-green-950",
    iconUrlOnDark: "https://cdn.simpleicons.org/geeksforgeeks/ffffff",
    iconUrl: "https://cdn.simpleicons.org/geeksforgeeks/2F8D46",
    poster: {
      canvas: "bg-gradient-to-br from-green-100 via-emerald-50 to-lime-100",
      canvasAlt: "bg-gradient-to-tr from-green-300/48 via-lime-100/50 to-emerald-200/44",
      orb: "bg-green-400/54",
      orb2: "bg-lime-500/42",
      accentLine: "from-green-500 via-emerald-500 to-teal-600",
    },
    filterChipActive: "border-green-700 bg-green-700 text-white",
  },
};

/** Poster-wall / billboard filter order — must match keys in providerStyles */
const KNOWN_PROVIDER_ORDER = ["leetcode", "codeforces", "codechef", "hackerrank", "topcoder", "atcoder", "geeksforgeeks"];

/** Light “whitish + brand tint” poster — merge with provider `poster` */
const defaultPosterTheme = {
  canvas: "bg-gradient-to-b from-slate-100/95 via-slate-50 to-slate-200/88",
  canvasAlt: "bg-gradient-to-tr from-slate-300/30 via-slate-100/22 to-indigo-100/28",
  orb: "bg-slate-400/28",
  orb2: "bg-indigo-200/26",
  titleShadow: "",
  accentLine: "from-slate-400 to-slate-600",
  metaLine: "text-slate-500",
  chipHairline: "via-slate-300/55",
  textTitle: "text-slate-900",
  textProvider: "text-slate-900",
  textSubline: "text-slate-600",
  metaMuted: "text-slate-500",
  textValue: "text-slate-800",
  divider: "divide-slate-200/90",
  iconChipWrap: "border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-200/55",
  opensPill:
    "border border-slate-200/85 bg-white/95 text-slate-800 shadow-sm ring-1 ring-slate-200/45",
  footerBar: "border-t border-slate-200/80 bg-white/85 backdrop-blur-sm",
};

/** Same hue as headline (`text-slate-900`); grid + featured billboard */
const posterCardOutlineClass =
  "ring-1 ring-slate-900/13 transition group-hover/poster:ring-slate-900/24";

const billboardOutlineClass =
  "ring-1 ring-slate-900/13 transition group-hover/bill:ring-slate-900/24";

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return "-";
  }

  const totalHours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);

  if (totalHours > 0 && mins > 0) {
    return `${totalHours}h ${mins}m`;
  }

  if (totalHours > 0) {
    return `${totalHours}h`;
  }

  return `${mins}m`;
};

const formatDateCompact = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const opts = {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  // Avoid “May 15” looking like *this* year when the API date is from another year (e.g. 2020).
  if (date.getFullYear() !== new Date().getFullYear()) {
    opts.year = "numeric";
  }

  return date.toLocaleString("en-IN", opts);
};

/** Strip spaces / map shorthand so API provider strings resolve to poster themes */
const PROVIDER_LOOKUP = /** @type {const} */ ({
  gfg: "geeksforgeeks",
});

const getProviderKey = (provider) => {
  if (provider == null || provider === "") {
    return "";
  }

  const compact = String(provider).trim().toLowerCase().replace(/\s+/g, "");

  const aliasTarget = PROVIDER_LOOKUP[compact];
  if (aliasTarget && providerStyles[aliasTarget]) {
    return aliasTarget;
  }

  return compact;
};

const capitalizeProviderSlug = (slug) =>
  slug ? `${slug.charAt(0).toUpperCase()}${slug.slice(1).toLowerCase()}` : "Other";

const formatTimeUntil = (value) => {
  if (!value) return "Time unavailable";

  const target = new Date(value).getTime();
  if (!Number.isFinite(target)) return "Time unavailable";

  const diff = target - Date.now();
  if (diff <= 0) return "Starting now";

  const mins = Math.floor(diff / 60000);
  const days = Math.floor(mins / (60 * 24));
  const hours = Math.floor((mins % (60 * 24)) / 60);
  const minutes = mins % 60;

  if (days > 0) return `Starts in ${days}d ${hours}h`;
  if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
  return `Starts in ${minutes}m`;
};

/** Subline under title: ended copy vs countdown to start */
const getContestTimelineSubline = (contest) => {
  if (contest?.status === "ENDED") {
    return "Contest ended";
  }
  return formatTimeUntil(contest?.startTime);
};

const getOpensPillLabel = (contest) => {
  if (contest?.status === "ENDED") {
    return `Started ${formatDateCompact(contest.startTime)}`;
  }
  return `Opens ${formatDateCompact(contest.startTime)}`;
};

const getCountdownParts = (value, nowTick) => {
  const target = new Date(value || 0).getTime();
  const now = nowTick;

  if (!Number.isFinite(target) || target <= now) {
    return { hours: "00", minutes: "00", seconds: "00" };
  }

  const diff = Math.floor((target - now) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  return {
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

const PlatformIcon = ({ providerKey, size = 14, tone = "default", fallbackLetter }) => {
  const ps = providerStyles[providerKey] || {};
  const iconUrl =
    tone === "onDark" && ps.iconUrlOnDark ? ps.iconUrlOnDark : ps.iconUrl;
  const fb = (fallbackLetter || "").trim();
  const fallback =
    fb.slice(0, 1).toUpperCase() || ps.label?.slice(0, 1)?.toUpperCase() || "?";

  const [imgFailed, setImgFailed] = useState(false);

  const invertOnDark = tone === "onDark" && ps.iconInvertOnDark;

  if (!iconUrl || imgFailed) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700"
        style={{ width: size, height: size }}
      >
        {fallback}
      </span>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={ps.label || "platform"}
      width={size}
      height={size}
      className={`rounded-sm object-contain ${invertOnDark ? "brightness-0 invert" : ""}`}
      loading="lazy"
      onError={() => setImgFailed(true)}
    />
  );
};

const EMPTY_STATE_BTN =
  "inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2";

const FeaturedOnlyEmptyUi = ({
  searchText,
  providerFilter,
  setSearchText,
  setProviderFilter,
}) => {
  const hasSearch = Boolean(searchText.trim());
  const hasFilter = providerFilter !== "all";

  const scrollToFeatured = () => {
    document
      .getElementById("contests-featured-billboard")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const scrollToGridToolbar = () => {
    document
      .getElementById("contests-upcoming-grid")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="px-5 py-6 text-center border rounded-xl border-slate-200 bg-slate-50/90 sm:px-6 sm:py-7">
      <p className="font-['Poppins',sans-serif] text-sm font-semibold text-slate-900 sm:text-base">
        No contests found
      </p>
      <p className="mt-1 text-xs text-slate-500">
        More cards appear when there are extra upcoming contests.
      </p>
      <div className="mt-5 flex flex-col items-center gap-2.5">
        <button type="button" onClick={scrollToFeatured} className={EMPTY_STATE_BTN}>
          <FaChevronUp className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          Next contest
        </button>
        <button
          type="button"
          onClick={scrollToGridToolbar}
          className="text-xs font-semibold underline transition text-slate-500 decoration-slate-300 underline-offset-2 hover:text-slate-700"
        >
          Back to filters
        </button>
      </div>
      {(hasSearch || hasFilter) && (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-5 mt-5 border-t border-slate-200/80">
          {hasFilter ? (
            <button type="button" onClick={() => setProviderFilter("all")} className={EMPTY_STATE_BTN}>
              <MdOutlineFilterAltOff className="w-4 h-4 shrink-0 text-slate-500" aria-hidden />
              All platforms
            </button>
          ) : null}
          {hasSearch ? (
            <button type="button" onClick={() => setSearchText("")} className={EMPTY_STATE_BTN}>
              <FaSearch className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
              Clear search
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

const GridFilterEmptyUi = ({
  providerFilter,
  setProviderFilter,
  searchText,
  setSearchText,
}) => {
  const plat =
    providerFilter !== "all"
      ? providerStyles[providerFilter]?.label || capitalizeProviderSlug(providerFilter)
      : "";

  const hasSearch = Boolean(searchText.trim());

  return (
    <div className="px-6 py-10 text-center border border-dashed rounded-2xl border-slate-300/90 bg-gradient-to-b from-white to-slate-50 sm:px-8">
      <div className="flex items-center justify-center mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-700 ring-1 ring-amber-100">
        <MdOutlineFilterAltOff className="h-7 w-7" aria-hidden />
      </div>
      <h3 className="mt-5 font-['Poppins',sans-serif] text-base font-semibold text-slate-900">
        {plat ? `No ${plat} contests in this list` : "No contests in this view"}
      </h3>
      <p className="max-w-sm mx-auto mt-2 text-sm text-slate-600">
        {hasSearch
          ? `Nothing matched “${searchText.trim()}” with the filters you chose. Relax or reset to see schedules again.`
          : "Relax the filters to see contests from every platform we're tracking."}
      </p>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
        {providerFilter !== "all" ? (
          <button type="button" onClick={() => setProviderFilter("all")} className={EMPTY_STATE_BTN}>
            <MdOutlineFilterAltOff className="w-4 h-4 shrink-0 text-slate-500" aria-hidden />
            All platforms
          </button>
        ) : null}
        {hasSearch ? (
          <button type="button" onClick={() => setSearchText("")} className={EMPTY_STATE_BTN}>
            Clear search
          </button>
        ) : null}
      </div>
    </div>
  );
};

const NoUpcomingDataEmptyUi = ({ searchText, setSearchText }) => {
  const hasSearch = Boolean(searchText.trim());

  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-indigo-50/25 to-slate-50/80 px-6 py-12 text-center sm:px-10">
      <div className="flex items-center justify-center w-16 h-16 text-indigo-600 shadow-sm rounded-2xl bg-indigo-50 ring-1 ring-indigo-100">
        <MdOutlineEventAvailable className="w-8 h-8" aria-hidden />
      </div>
      <p className="mt-6 font-['Poppins',sans-serif] text-lg font-semibold text-slate-900 sm:text-xl">
        No upcoming contests yet
      </p>
      <p className="max-w-md mt-2 text-sm leading-relaxed text-slate-600">
        Sync may still be fetching the latest schedules, or there simply aren&apos;t any future rounds matching your criteria right now.
      </p>
      {hasSearch ? (
        <button type="button" onClick={() => setSearchText("")} className={`${EMPTY_STATE_BTN} mt-8`}>
          <FaSearch className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          Clear search &amp; retry
        </button>
      ) : (
        <p className="mt-6 max-w-xs text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
          Tip: contests refresh periodically — check back soon
        </p>
      )}
    </div>
  );
};

const ContestPosterCard = ({ contest }) => {
  const providerKey = getProviderKey(contest.provider);
  const ps = providerStyles[providerKey] || {};
  const poster = { ...defaultPosterTheme, ...(ps.poster || {}) };
  const providerLabel = ps.label || contest.provider;
  const linkClass = ps.link || "text-slate-800 hover:text-slate-950";
  const isEnded = contest?.status === "ENDED";

  return (
    <article className="group/poster relative isolate flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_28px_-18px_rgba(15,23,42,0.35)] transition duration-300 ease-out hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_18px_36px_-20px_rgba(15,23,42,0.35)]">
      <a
        href={contest.url}
        target="_blank"
        rel="noreferrer"
        className="absolute inset-0 z-[1] rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
        aria-label={`Open contest: ${contest.title}`}
      />
      <div className="relative z-[2] flex min-h-0 flex-1 flex-col pointer-events-none">
      <div className="relative overflow-hidden isolate px-4 pb-5 pt-4">
        <div className={`absolute inset-0 ${poster.canvas}`} aria-hidden />
        <div className={`absolute inset-0 ${poster.canvasAlt}`} aria-hidden />
        <div className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl ${poster.orb}`} aria-hidden />
        <div className={`pointer-events-none absolute -bottom-16 -left-12 h-36 w-36 rounded-full blur-3xl ${poster.orb2}`} aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_60%_at_50%_0%,rgba(255,255,255,0.45),transparent_65%)]" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-9 pointer-events-none bg-gradient-to-b from-transparent to-white/70" aria-hidden />

        <div className="relative z-10">
          {/* Top row: icon + provider + countdown chip */}
          <div className="flex items-center justify-between gap-2 font-['Poppins',sans-serif]">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl p-1 shadow-sm ${poster.iconChipWrap}`}>
                <PlatformIcon providerKey={providerKey} size={20} tone="default" fallbackLetter={contest.provider} />
              </span>
              <p className={`truncate text-[11px] font-black uppercase tracking-[0.16em] ${poster.textProvider}`}>
                {providerLabel}
              </p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200/80 bg-white/95 px-2.5 py-0.5 text-[9.5px] font-bold tabular-nums shadow-sm ${isEnded ? "text-slate-500" : "text-slate-800"}`}>
              {isEnded ? (
                <FaCheckCircle className="h-2.5 w-2.5 text-slate-400" aria-hidden />
              ) : (
                <FaRegClock className="h-2.5 w-2.5 text-slate-500" aria-hidden />
              )}
              {getContestTimelineSubline(contest)}
            </span>
          </div>

          {/* Title block */}
          <div className="mt-5 flex items-start justify-between gap-3">
            <h3
              className={`line-clamp-2 min-w-0 text-balance text-[1rem] font-bold uppercase leading-[1.18] tracking-[0.01em] ${poster.textTitle}`}
              title={contest.title}
            >
              {contest.title}
            </h3>
          </div>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col justify-end bg-white px-4 pb-4 pt-4">
        <div className="grid grid-cols-3 items-stretch divide-x divide-slate-200/80 rounded-xl border border-slate-200/80 bg-slate-50/90 px-1.5 py-3">
          <div className="min-w-0 px-1.5 text-center">
            <p className="text-[8.5px] font-black uppercase tracking-[0.18em] text-slate-500">
              {isEnded ? "Started" : "Opens"}
            </p>
            <p className="mt-0.5 truncate text-[11.5px] font-bold tabular-nums text-slate-900">
              {formatDateCompact(contest.startTime)}
            </p>
          </div>
          <div className="min-w-0 px-1.5 text-center">
            <p className="text-[8.5px] font-black uppercase tracking-[0.18em] text-slate-500">Ends</p>
            <p className="mt-0.5 truncate text-[11.5px] font-bold tabular-nums text-slate-900">
              {formatDateCompact(contest.endTime)}
            </p>
          </div>
          <div className="min-w-0 px-1.5 text-center">
            <p className="text-[8.5px] font-black uppercase tracking-[0.18em] text-slate-500">Duration</p>
            <p className="mt-0.5 truncate text-[11.5px] font-bold tabular-nums text-slate-900">
              {formatDuration(contest.duration)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-end pr-1.5">
          {(() => {
            const idleLabel = isEnded ? "View" : "Register";
            const hoverLabel = "Open";
            return (
              <span
                className={`relative inline-flex items-center gap-1.5 rounded-full font-['Poppins',sans-serif] text-[10.5px] font-black uppercase tracking-[0.15em] transition-colors duration-300 ${linkClass}`}
              >
                <span className="relative inline-grid overflow-hidden leading-[1.15] text-left">
                  <span className="col-start-1 row-start-1 whitespace-nowrap transition-transform duration-[480ms] ease-[cubic-bezier(0.7,0,0.3,1)] group-hover/poster:-translate-y-full">
                    {idleLabel}
                  </span>
                  <span
                    aria-hidden
                    className="col-start-1 row-start-1 translate-y-full whitespace-nowrap transition-transform duration-[480ms] ease-[cubic-bezier(0.7,0,0.3,1)] group-hover/poster:translate-y-0"
                  >
                    {hoverLabel}
                  </span>
                </span>

                <span
                  aria-hidden
                  className="relative inline-flex h-3.5 w-3.5 items-center justify-center shrink-0"
                >
                  <HiArrowRight className="h-3.5 w-3.5 transition-transform duration-[480ms] ease-[cubic-bezier(0.7,0,0.3,1)] group-hover/poster:-translate-y-0.5 group-hover/poster:translate-x-0.5 group-hover/poster:-rotate-45" />
                </span>
              </span>
            );
          })()}
        </div>
      </div>
      </div>
    </article>
  );
};

const Contests = () => {
  const [searchText, setSearchText] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { data, loading, error } = useQuery(GET_ALL_CONTESTS, {
    fetchPolicy: "cache-and-network",
  });

  const contests = data?.allContests || [];

  const searchedContests = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return contests
      .filter((contest) => contest?.status === "UPCOMING")
      .filter((contest) => {
        if (!query) {
          return true;
        }

        const title = contest?.title?.toLowerCase() || "";
        const provider = contest?.provider?.toLowerCase() || "";

        return title.includes(query) || provider.includes(query);
      })
      .sort((a, b) => {
        const aMs = new Date(a?.startTime || 0).getTime();
        const bMs = new Date(b?.startTime || 0).getTime();
        return aMs - bMs;
      });
  }, [contests, searchText]);

  const filteredContests = useMemo(() => {
    if (providerFilter === "all") return searchedContests;
    return searchedContests.filter(
      (contest) => getProviderKey(contest?.provider) === providerFilter
    );
  }, [searchedContests, providerFilter]);

  const counts = useMemo(() => {
    const byKnown = Object.fromEntries(
      KNOWN_PROVIDER_ORDER.map((k) => [k, 0])
    );
    const unknownByProvider = {};

    for (const contest of searchedContests) {
      const pk = getProviderKey(contest?.provider);
      if (!pk) continue;

      if (KNOWN_PROVIDER_ORDER.includes(pk)) {
        byKnown[pk]++;
      } else {
        unknownByProvider[pk] = (unknownByProvider[pk] || 0) + 1;
      }
    }

    return {
      TOTAL: searchedContests.length,
      ...byKnown,
      unknownByProvider,
    };
  }, [searchedContests]);

  const posterWallFilterButtons = useMemo(() => {
    const extraFromData = Object.keys(counts.unknownByProvider || {}).filter(
      (slug) =>
        KNOWN_PROVIDER_ORDER.includes(slug) === false &&
        (counts.unknownByProvider?.[slug] || 0) > 0
    );

    return [
      {
        key: "all",
        label: "All",
        count: counts.TOTAL,
        activeCls: "border-slate-900 bg-slate-900 text-white",
        iconKey: null,
      },
      ...KNOWN_PROVIDER_ORDER.map((key) => ({
        key,
        label: providerStyles[key]?.label || key,
        count: counts[key] ?? 0,
        activeCls:
          providerStyles[key]?.filterChipActive ||
          "border-slate-700 bg-slate-700 text-white",
        iconKey: key,
      }))
        .filter((row) => row.count > 0),
      ...extraFromData.map((key) => ({
        key,
        label: capitalizeProviderSlug(key),
        count: counts.unknownByProvider[key],
        activeCls: "border-slate-600 bg-slate-600 text-white",
        iconKey: key,
      })),
    ];
  }, [counts]);

  const nextContest = filteredContests[0] || null;
  const countdown = getCountdownParts(nextContest?.startTime, nowTick);
  const gridContests = nextContest
    ? filteredContests.filter(
        (contest) =>
          (contest.id || contest.providerContestId) !==
          (nextContest.id || nextContest.providerContestId)
      )
    : filteredContests;

  return (
    <StudentLayout>
      <div className="min-w-0 font-['Poppins',sans-serif]">
        <NetworkIndicator />

        <section className="min-h-screen px-2 py-4 bg-white md:px-4 lg:px-6">
        <div className="mx-auto w-full max-w-[1480px] space-y-3">
          <header className="overflow-hidden bg-white rounded-2xl">
            <div className="p-3.5 md:p-4.5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Contest Tracker</p>
                  <h1 className="mt-1 text-2xl font-semibold leading-tight text-slate-950">Coding contests</h1>
                  <p className="mt-1 text-xs text-slate-600">
                    Upcoming rounds across LeetCode, Codeforces, CodeChef, and other platforms.
                  </p>
                </div>

                <div className="flex flex-col gap-2.5 md:items-end">
                  <div className="relative w-full md:w-[300px] lg:w-[340px]">
                    <FaSearch className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search contests"
                      className="w-full py-2 pl-10 pr-3 text-xs transition bg-white border outline-none h-9 rounded-xl border-slate-300 text-slate-700 focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                      Total {counts.TOTAL}
                    </span>
                    {KNOWN_PROVIDER_ORDER.filter((k) => (counts[k] || 0) > 0).map((k) => (
                      <span
                        key={k}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${providerStyles[k]?.badge || "border-slate-200 bg-slate-50 text-slate-800"}`}
                      >
                        <PlatformIcon providerKey={k} size={12} />
                        {counts[k]}
                      </span>
                    ))}
                    {Object.entries(counts.unknownByProvider || {})
                      .filter(([, n]) => n > 0)
                      .map(([slug]) => (
                        <span
                          key={slug}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-800"
                        >
                          <PlatformIcon
                            providerKey={slug}
                            size={12}
                            fallbackLetter={slug}
                          />
                          {counts.unknownByProvider[slug]}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-slate-200 bg-white">
              <LightSpinner message="Loading contests" />
            </div>
          ) : error ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-lg font-semibold text-red-700">Unable to fetch contests</p>
              <p className="mt-1 text-sm text-red-600">Please try again in a moment.</p>
            </div>
          ) : searchedContests.length > 0 ? (
            <>
              {nextContest ? (
                <section id="contests-featured-billboard" className="mt-2 mb-8 scroll-mt-24">
                  <p className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.75)]" />
                    Next contests
                  </p>
                  {(() => {
                    const key = getProviderKey(nextContest.provider);
                    const providerLabel = providerStyles[key]?.label || nextContest.provider;
                    const btnClass = providerStyles[key]?.button || "bg-slate-800 hover:bg-slate-900";
                    const poster = { ...defaultPosterTheme, ...(providerStyles[key]?.poster || {}) };

                    return (
                      <article
                        className={`group/bill relative mr-auto w-full max-w-[75%] overflow-hidden rounded-3xl shadow-[0_12px_44px_-12px_rgba(15,23,42,0.12)] transition duration-300 hover:-translate-y-0.5 sm:rounded-[1.65rem] ${billboardOutlineClass}`}
                      >
                        <div className={`absolute inset-0 ${poster.canvas}`} />
                        <div className={`absolute inset-0 ${poster.canvasAlt}`} />
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_98%_72%_at_12%_-2%,rgba(255,255,255,0.38),transparent_55%)]" />
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/22 via-transparent to-slate-100/24" />
                        <div className={`pointer-events-none absolute -left-24 top-16 h-[min(520px,80vw)] w-[min(520px,80vw)] rounded-full blur-3xl ${poster.orb}`} />
                        <div className={`pointer-events-none absolute -bottom-32 right-[-10%] h-72 w-72 rounded-full blur-3xl ${poster.orb2}`} />
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(248,250,252,0.38)_0%,transparent_62%)]" />
                        <div className="pointer-events-none absolute inset-0 opacity-[0.35] bg-[repeating-linear-gradient(180deg,transparent,transparent_3px,rgba(148,163,184,0.04)_3px,rgba(148,163,184,0.04)_6px)] mix-blend-multiply" />

                        <div className="relative z-10 flex min-h-[220px] flex-col gap-7 p-5 pt-6 sm:p-7 sm:pt-7 lg:grid lg:min-h-[260px] lg:grid-cols-[1.15fr_auto] lg:items-start lg:gap-10 lg:p-9 lg:pt-8">
                          <div className="max-w-[52rem]">
                            <div className="inline-flex items-center gap-3 font-['Poppins',sans-serif]">
                              <span
                                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl p-1 sm:h-11 sm:w-11 ${poster.iconChipWrap}`}
                              >
                                <PlatformIcon
                                  providerKey={key}
                                  size={24}
                                  tone="default"
                                  fallbackLetter={nextContest.provider}
                                />
                              </span>
                              <span
                                className={`text-[11px] font-black uppercase tracking-[0.2em] sm:text-[12px] ${poster.textProvider}`}
                              >
                                {providerLabel}
                              </span>
                            </div>

                            <h2
                              className={`mt-5 text-balance text-[clamp(1.25rem,2.55vw,1.85rem)] font-semibold uppercase leading-[1.06] tracking-[0.03em] ${poster.textTitle} ${poster.titleShadow}`}
                            >
                              {nextContest.title}
                            </h2>
                            <p className={`mt-2 max-w-2xl text-sm font-semibold ${poster.textSubline}`}>
                              {formatTimeUntil(nextContest.startTime)}
                            </p>

                            <div className="mt-6 grid gap-5 border-t border-slate-200/75 pt-6 font-['Poppins',sans-serif] sm:grid-cols-3 sm:gap-4 lg:gap-6">
                              {[
                                {
                                  icon: RiCalendarEventLine,
                                  label: "Starts",
                                  value: formatDateTime(nextContest.startTime),
                                },
                                {
                                  icon: MdOutlineEventAvailable,
                                  label: "Ends",
                                  value: formatDateTime(nextContest.endTime),
                                },
                                {
                                  icon: FaRegClock,
                                  label: "Duration",
                                  value: formatDuration(nextContest.duration),
                                },
                              ].map((row) => {
                                const Icon = row.icon;
                                return (
                                  <div key={row.label} className="flex min-w-0 items-start gap-3 sm:gap-3.5">
                                    <Icon
                                      className="mt-0.5 size-[1.125rem] shrink-0 text-slate-500 sm:mt-1 sm:size-5"
                                      aria-hidden
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p
                                        className={`text-[10px] font-bold uppercase tracking-[0.14em] sm:text-[11px] ${poster.metaLine}`}
                                      >
                                        {row.label}
                                      </p>
                                      <p
                                        className={`mt-0.5 text-[11px] font-bold leading-tight tracking-tight tabular-nums sm:text-[12px] lg:text-[13px] ${poster.textValue}`}
                                      >
                                        {row.value}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex w-full shrink-0 flex-col gap-4 lg:w-auto lg:min-w-[260px]">
                            <div className="p-4 border shadow-inner rounded-3xl border-slate-200/85 bg-white/90 shadow-slate-200/50 backdrop-blur-sm">
                              <p className={`text-[9px] font-black uppercase tracking-[0.35em] ${poster.metaLine}`}>
                                Countdown
                              </p>
                              <div className="flex items-center justify-center gap-2 mt-3 sm:justify-between lg:justify-center xl:justify-between">
                                {[
                                  { label: "Hrs", value: countdown.hours },
                                  { label: "Min", value: countdown.minutes },
                                  { label: "Sec", value: countdown.seconds },
                                ].map((item, idx) => (
                                  <React.Fragment key={item.label}>
                                    <div className="min-w-[calc(33.333%-6px)] flex-1 rounded-2xl border border-slate-200/85 bg-white px-1.5 py-2 text-center shadow-sm">
                                      <p className="text-xl font-black leading-none text-slate-900 tabular-nums sm:text-2xl">{item.value}</p>
                                      <p className="mt-2 text-[8px] font-bold uppercase tracking-widest text-slate-500">{item.label}</p>
                                    </div>
                                    {idx < 2 ? (
                                      <span className="text-lg font-black select-none text-slate-300">:</span>
                                    ) : null}
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>

                            <a
                              href={nextContest.url}
                              target="_blank"
                              rel="noreferrer"
                              className={`group/btn inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200/80 py-3 font-['Poppins',sans-serif] text-sm font-black uppercase tracking-[0.28em] text-white shadow-sm transition hover:brightness-105 active:scale-[0.99] ${btnClass}`}
                            >
                              Enter contest
                              <HiArrowRight className="transition-transform duration-200 group-hover/btn:translate-x-1" aria-hidden />
                            </a>
                          </div>
                        </div>
                      </article>
                    );
                  })()}
                </section>
              ) : null}

              <section id="contests-upcoming-grid" className="mt-4 scroll-mt-24">
                <div className="my-3 flex flex-wrap items-center justify-between gap-2.5 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 sm:text-[11px]">
                    Upcoming contests
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    {posterWallFilterButtons.map((opt) => {
                      const isActive = providerFilter === opt.key;
                      const showIcon = opt.key !== "all" && opt.iconKey;

                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setProviderFilter(opt.key)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition sm:text-[12px] ${
                            isActive
                              ? opt.activeCls
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {showIcon ? (
                            <PlatformIcon
                              providerKey={opt.iconKey}
                              size={13}
                              fallbackLetter={opt.iconKey}
                            />
                          ) : null}
                          {opt.label}
                          <span
                            className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold sm:text-[11px] ${
                              isActive ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {opt.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {gridContests.length === 0 ? (
                  nextContest ? (
                    <FeaturedOnlyEmptyUi
                      searchText={searchText}
                      providerFilter={providerFilter}
                      setSearchText={setSearchText}
                      setProviderFilter={setProviderFilter}
                    />
                  ) : (
                    <GridFilterEmptyUi
                      providerFilter={providerFilter}
                      setProviderFilter={setProviderFilter}
                      searchText={searchText}
                      setSearchText={setSearchText}
                    />
                  )
                ) : (
                <div className="grid items-stretch grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-7">
                  {gridContests.map((contest) => (
                    <ContestPosterCard key={contest.id || contest.providerContestId} contest={contest} />
                  ))}
                </div>
                )}
              </section>
            </>
          ) : (
            <NoUpcomingDataEmptyUi searchText={searchText} setSearchText={setSearchText} />
          )}
        </div>
      </section>
      </div>
    </StudentLayout>
  );
};

export default Contests;